import { clamp, HintAnimation } from '../hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  DIVIDER_H,
  DIVIDER_V,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  orbi3d,
  ORBI_FACE_SECONDS,
  paintOrbiFace,
  PRESSED,
  reliefFolder,
  Refs,
  renderCamera,
  renderCursor,
  stage,
} from './scene-kit';

/**
 * Portal-Hinweis 2 „Verknüpfe deinen KI-Agenten mit deiner Wissensbasis" —
 * Port des Claude-Design-Films `Agent Wissen Loop.dc.html` (Projekt
 * „Portal-Hinweis 2", Datei `agent-wissen-loop.jsx`). Bühne 640×354,
 * 8,0 s Loop; T = Abspielzeit / 1,6 wie bei Hinweis 1.
 */

const STRETCH = 1.6;
const AUTHORED = 5.0;

// Szenenanfänge in Autorenzeit (Summe der `nat`-Längen aus OM_SCENES).
const CUES = { Oeffnen: 0.7, Ordner: 1.2, Anhaken: 2.0, Verknuepfen: 2.9, Fertig: 3.8, Loop: 4.4 };

// Panel-Geometrie (Glas-Koordinaten; Glas liegt bei PX/PY auf der Bühne)
const PX = 20, PY = 18, PW = 600, PH = 318;
const NAV: [string, string][] = [
  ['sliders', 'Allgemein'],
  ['book', 'Wissen'],
  ['template', 'Lernprozess'],
  ['brain', 'Verhalten &amp; Fähigkeiten'],
];
const SEL = 1;
const NAV_Y = (i: number) => 82 + i * 42;
const RX = 228, RW = 356;
const TABS: [string, number, number][] = [['Wissensbasis', RX, 112], ['Quellen &amp; Suche', RX + 112, 136]];
const TILES = ['Prüfungen', 'Skripte WS 26', 'FAQ Studienbüro'];
const TILE_W = 114, TILE_H = 104, TILE_Y = 112;
const TILE_X = (i: number) => RX + i * (TILE_W + 7);
const CHK = (i: number) => ({ x: TILE_X(i) + TILE_W - 26, y: TILE_Y + 14 }); // Checkbox 18×18
const ORBI = { x: 24, y: 22, size: 34 };

// Sechs Dokument-Partikel: je drei aus „Prüfungen" und „Skripte WS 26".
const PARTICLES = [0, 1, 2].flatMap((j) =>
  [0, 1].map((i) => ({ order: j * 2 + i, from: { x: TILE_X(i) + 29 + j * 8, y: TILE_Y + 29 } })),
);

function template(uid: string): string {
  const nav =
    `<div style="${abs(14, 22, 180, 34, `display:flex;align-items:center;gap:10px;padding-left:48px;box-sizing:border-box;font-size:15px;font-weight:600;color:${C.text};white-space:nowrap;`)}">Statistik-Tutor</div>` +
    `<div style="${abs(14, 68, 182, 2, `background:${DIVIDER_H};`)}"></div>` +
    NAV.map(
      ([ic, label], i) =>
        `<div style="${abs(10, NAV_Y(i), 190, 36, 'display:flex;')}">` +
        (i === SEL ? `<div data-r="navSel" style="position:absolute;inset:0;border-radius:14px;opacity:0;${PRESSED}"></div>` : '') +
        `<div ${i === SEL ? 'data-r="navLabel"' : ''} style="position:relative;flex:1;display:flex;align-items:center;gap:9px;padding:0 11px;font-size:13px;font-weight:500;color:${C.text};white-space:nowrap;">` +
        `${icon(ic, 17)}${label}</div></div>`,
    ).join('');

  const header =
    `<div data-r="header" style="opacity:0;">` +
    TABS.map(
      ([label, x, w], i) =>
        `<div style="${abs(x, 22, w, 30, `display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:${i === 0 ? 600 : 500};color:${i === 0 ? C.text : C.muted};white-space:nowrap;`)}">${label}</div>`,
    ).join('') +
    `<div style="${abs(RX, 54, RW, 2, `background:${DIVIDER_H};`)}"></div>` +
    `<div data-r="tabUnder" style="${abs(RX + 10, 52, TABS[0][2] - 20, 3, `border-radius:2px;background:${C.action};opacity:0;`)}"></div>` +
    `<div style="${abs(RX + 2, 72, RW, 22, `font-size:16px;font-weight:600;color:${C.text};white-space:nowrap;`)}">Wissensbasis – Verknüpfung</div>` +
    `</div>`;

  const tiles = TILES.map(
    (name, i) =>
      `<div data-r="tile" style="${abs(TILE_X(i), TILE_Y, TILE_W, TILE_H, 'opacity:0;')}">` +
      `<div data-r="tileFace" style="position:absolute;inset:0;border-radius:18px;background:linear-gradient(150deg, rgb(255 255 255 / .34), rgb(255 255 255 / .12));"></div>` +
      `<div style="${abs(12, 12, 34, 34)}">` +
      reliefFolder(
        `<div data-r="folderOff" style="position:absolute;inset:0;">${icon('folder', 20)}</div>`,
        `<div data-r="folderOn" style="position:absolute;inset:0;opacity:0;">${icon('folder', 20, C.action)}</div>`,
      ) +
      `</div>` +
      `<div style="${abs(TILE_W - 26, 14, 18, 18)}"><div style="position:relative;width:18px;height:18px;">` +
      `<div style="position:absolute;inset:0;border-radius:6px;background:rgb(255 255 255 / .55);box-shadow:inset 0 2px 3px rgb(47 76 121 / .22), inset 0 -1px 1px rgb(255 255 255 / .9), 0 0 0 1px rgb(83 109 145 / .28);"></div>` +
      `<div data-r="check" style="position:absolute;inset:0;border-radius:6px;background:${C.action};opacity:0;box-shadow:0 3px 6px -3px rgb(37 99 235 / .6);display:flex;align-items:center;justify-content:center;">${icon('check', 13, '#fff', 2.6)}</div>` +
      `</div></div>` +
      `<div style="${abs(10, 66, TILE_W - 14, 22, `font-size:12px;font-weight:600;color:${C.text};white-space:nowrap;`)}">${name}</div>` +
      `</div>`,
  ).join('');

  const saved =
    `<div data-r="saved" style="${abs(RX + 2, TILE_Y + TILE_H + 22, RW, 18, `display:flex;align-items:center;gap:8px;opacity:0;font-size:12px;font-weight:500;color:${C.muted};`)}">` +
    `<span style="width:7px;height:7px;border-radius:999px;background:${C.ok};"></span>Wird sofort gespeichert</div>`;

  const particles = PARTICLES.map(
    () =>
      `<div data-r="particle" style="${abs(0, 0, 22, 26, 'display:none;border-radius:7px;background:rgb(255 255 255 / .9);box-shadow:0 4px 8px -4px rgb(38 65 112 / .35), inset 0 0 0 1px rgb(255 255 255 / .9);align-items:center;justify-content:center;')}">` +
      `${icon('file', 14, C.accent, 2)}</div>`,
  ).join('');

  const orbi =
    `<div data-r="orbi" style="${abs(ORBI.x, ORBI.y, ORBI.size, ORBI.size)}">` +
    `<div data-r="orbiBall" style="position:absolute;inset:0;border-radius:999px;"></div>` +
    orbi3d(ORBI.size, 'eye', 'smile', 'front') +
    `</div>`;

  const panel = glass(
    30,
    0.42,
    `left:${PX}px;top:${PY}px;width:${PW}px;height:${PH}px;`,
    nav + `<div style="${abs(208, 20, 2, PH - 40, `background:${DIVIDER_V};`)}"></div>` + header + tiles + saved + particles + orbi,
  );

  return stage(panel + cursorTemplate(uid));
}

export class LinkKnowledgeBaseAnimation implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds = AUTHORED * STRETCH;
  /** Schlussbild: zwei Ordner angehakt, Orbi lacht, „Wird sofort gespeichert" (T = 4,2). */
  readonly stillAt = 4.2 * STRETCH;

  private refs: Refs = {};

  mount(host: HTMLElement): void {
    this.refs = mountTemplate(host, template(nextUid('al-hint-link')));
  }

  render(t: number): void {
    this.frame(clamp(t / STRETCH, 0, AUTHORED));
  }

  private one(key: string): HTMLElement {
    return this.refs[key][0];
  }

  /** Ein Bild zur Autorenzeit T — Werte wie `Piece` in agent-wissen-loop.jsx. */
  private frame(T: number): void {
    const { Oeffnen: oe, Ordner: od, Anhaken: ah, Verknuepfen: vk, Fertig: fe, Loop: lp } = CUES;
    const live = 1 - MOTION.glide(lp, lp + 0.5, T);

    const a1 = ah + 0.3, a2 = ah + 0.7;
    const k1 = click(oe, T), k2 = click(a1, T), k3 = click(a2, T);
    const press = Math.max(k1.press, k2.press, k3.press);
    const ring = [k1, k2, k3].map((k) => k.ring).find((r) => r > 0 && r < 1) || 0;

    // Zeiger in Bühnenkoordinaten (Glas + PX/PY)
    const REST = [548, 292];
    const W = [PX + 74, PY + NAV_Y(SEL) + 18];
    const B1 = [PX + CHK(0).x + 9, PY + CHK(0).y + 9];
    const B2 = [PX + CHK(1).x + 9, PY + CHK(1).y + 9];
    const ct = [0, oe - 0.04, oe + 0.08, ah + 0.22, a1 + 0.08, a2 - 0.06, a2 + 0.1, vk + 0.4, 99];
    const pts = [REST, W, W, B1, B1, B2, B2, REST, REST];
    const cx = MOTION.path(ct, pts.map((p) => p[0]), T);
    const cy = MOTION.path(ct, pts.map((p) => p[1]), T);

    const sel = MOTION.enter(oe, oe + 0.12, T) * live;
    const header = MOTION.enter(oe + 0.05, oe + 0.35, T) * live;
    const tabUnder = MOTION.enter(oe + 0.18, oe + 0.4, T) * live;

    // Kamera
    const ck = [0, oe, od + 0.1, ah + 0.1, a2 + 0.2, vk + 0.3, 99];
    const s = MOTION.path(ck, [1, 1.06, 1.04, 1.04, 1.04, 1, 1], T);
    const fx = MOTION.path(ck, [320, 230, 345, 345, 345, 320, 320], T);
    const fy = MOTION.path(ck, [176, 140, 170, 160, 160, 176, 176], T);
    renderCamera(this.one('camera'), fx, fy, s);

    // Agent-Menü
    this.one('navSel').style.opacity = String(sel);
    this.one('navLabel').style.fontWeight = sel > 0.5 ? '600' : '500';

    // Reiter + Überschrift
    const head = this.one('header');
    head.style.opacity = String(header);
    head.style.transform = `translateY(${(1 - header) * 6}px)`;
    this.one('tabUnder').style.opacity = String(tabUnder);

    // Ordner-Kacheln
    this.refs['tile'].forEach((el, i) => {
      const st = od + 0.12 + 0.16 * i;
      const at = i === 0 ? a1 : i === 1 ? a2 : null;
      const on = at == null ? 0 : MOTION.enter(at, at + 0.12, T) * live;
      const g = at == null ? 0 : clamp((T - at) / 0.5, 0, 1);
      const glow = Math.sin(Math.PI * g);
      const o = MOTION.enter(st, st + 0.32, T) * live;
      el.style.opacity = String(o);
      el.style.transform = `translateY(${(1 - o) * 10}px) scale(${0.96 + 0.04 * o})`;
      this.refs['tileFace'][i].style.boxShadow =
        `inset 0 1px 1px rgb(255 255 255 / .85), inset 0 -1px 1px rgb(52 76 117 / .10), ` +
        `0 0 0 ${2 * glow}px rgb(59 130 246 / ${0.55 * glow}), 0 0 ${20 * glow}px rgb(59 130 246 / ${0.45 * glow})`;
      this.refs['folderOff'][i].style.opacity = String(1 - on);
      this.refs['folderOn'][i].style.opacity = String(on);
      const check = this.refs['check'][i];
      check.style.opacity = String(on);
      check.style.transform = `scale(${0.7 + 0.3 * on})`;
    });

    // Dokument-Partikel: quadratische Kurve von der Kachel zu Orbi
    const to = { x: ORBI.x + 17, y: ORBI.y + 17 };
    let pop = 0;
    PARTICLES.forEach(({ order, from }, n) => {
      const st = vk + 0.05 + 0.07 * order;
      const p = clamp((T - st) / 0.42, 0, 1);
      const d = (T - (st + 0.42)) / 0.18;
      pop = Math.max(pop, d > 0 && d < 1 ? Math.sin(Math.PI * d) : 0);
      const el = this.refs['particle'][n];
      if (p <= 0 || p >= 1) {
        el.style.display = 'none';
        return;
      }
      const ctl = { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 34 };
      const u = 1 - p;
      const x = u * u * from.x + 2 * u * p * ctl.x + p * p * to.x;
      const y = u * u * from.y + 2 * u * p * ctl.y + p * p * to.y;
      el.style.display = 'flex';
      el.style.left = `${x - 11}px`;
      el.style.top = `${y - 13}px`;
      el.style.opacity = String(Math.min(1, p * 6) * Math.min(1, (1 - p) * 5));
      el.style.transform = `scale(${1 - 0.45 * p}) rotate(${(1 - p) * -14}deg)`;
    });

    // Orbi fängt die Dokumente und lacht
    const laugh = paintOrbiFace(this.refs['eye'], this.refs['front'], this.refs['smile'], MOTION.glide(vk + 0.3, vk + 0.3 + ORBI_FACE_SECONDS, T) * live);
    const lift = -3 * laugh * Math.abs(Math.sin(Math.PI * 3 * laugh)) - 2.5 * pop;
    this.one('orbi').style.transform = `translateY(${lift}px) scale(${1 + 0.06 * pop})`;
    this.one('orbiBall').style.boxShadow =
      `0 6px 12px -6px rgb(38 65 112 / .35), 0 0 ${14 * pop}px rgb(143 132 228 / ${0.5 * pop})`;

    // „Wird sofort gespeichert"
    const saved = MOTION.enter(fe - 0.25, fe - 0.02, T) * live;
    const savedEl = this.one('saved');
    savedEl.style.opacity = String(saved);
    savedEl.style.transform = `translateY(${(1 - saved) * 4}px)`;

    renderCursor(this.one('ring'), this.one('arrow'), cx, cy, press, ring);
  }
}
